import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ChevronDown, CirclePlus, Plus, RotateCcw, Users } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { PersonData } from "@/types/api";
import type { RegionAnalysis } from "@/types/report";
import {
  Card,
  Button,
  Input,
  DateInput,
  AddressSuggest,
  PassportInput,
  DriversLicenseInput,
  InnInput,
  SnilsInput,
  ValidationIndicator,
} from "@/components/atoms";
import type { AddressData } from "@/components/atoms";
import {
  AccordionSection,
  FieldGroup,
  CollapsedFieldsTable,
  Cake,
  MultiPhoneInput,
  MultiEmailInput,
  MultiAddressInput,
} from "@/components/molecules";
import type { CollapsedFieldData } from "@/components/molecules";
import { useFieldValidation, useRegionAnalysis } from "@/hooks";
import { cn, formatDateWithAge } from "@/utils";

interface TestPersonOption {
  label: string;
  description: string;
  data: PersonData;
}

interface PersonFormProps {
  initialData?: PersonData;
  onSubmit: (data: PersonData) => void;
  onChange?: (data: PersonData) => void;
  onRegionChange?: (regionAnalysis: RegionAnalysis | null) => void;
  isLoading?: boolean;
  withCard?: boolean;
  header?: React.ReactNode;
  hideSubmitButton?: boolean;
  /** Строка настроек проверки над кнопкой "Начать проверку" (страна, матрица рисков) */
  submitToolbar?: React.ReactNode;
  /** Контролы в строке действий слева от кнопки "Начать проверку" (выбор тарифа) */
  submitAddon?: React.ReactNode;
  /** Опции тестовых данных для выпадающего меню */
  testPersons?: TestPersonOption[];
  /** Callback при выборе тестовой персоны */
  onSelectTestPerson?: (person: TestPersonOption) => void;
  /** Callback для сброса формы */
  onReset?: () => void;
}

const MAX_PHONES = 5;
const MAX_EMAILS = 5;
const MAX_ADDRESSES = 5;

type FieldId =
  | "birth_date"
  | "birth_place"
  | "passport"
  | "drivers_license"
  | "inn"
  | "snils"
  | "phone"
  | "email"
  | "address";

// Типы полей которые могут сворачиваться
type CollapsibleFieldId =
  | "name"
  | "birth_date"
  | "birth_place"
  | "passport"
  | "drivers_license"
  | "inn"
  | "snils"
  | `phone_${number}`
  | `email_${number}`
  | `address_${number}`;

interface FieldOption {
  id: FieldId;
  label: string;
  accordion: "personal" | "documents" | "contacts";
}

const fieldOptions: FieldOption[] = [
  { id: "birth_date", label: "Дата рождения", accordion: "personal" },
  { id: "birth_place", label: "Место рождения", accordion: "personal" },
  { id: "passport", label: "Паспорт РФ", accordion: "documents" },
  { id: "drivers_license", label: "Водительское удостоверение", accordion: "documents" },
  { id: "inn", label: "ИНН", accordion: "documents" },
  { id: "snils", label: "СНИЛС", accordion: "documents" },
  { id: "phone", label: "Телефон", accordion: "contacts" },
  { id: "email", label: "E-mail", accordion: "contacts" },
  { id: "address", label: "Адрес", accordion: "contacts" },
];

/** Добавление поля: тихая текстовая кнопка — не должна спорить с полями формы. */
const ADD_FIELD_BUTTON = cn(
  "inline-flex items-center gap-1 h-8 px-2 rounded-lg text-fs-sm text-ink-2",
  "hover:bg-surface-hover hover:text-ink-1 transition-colors",
);

/** Квадратная кнопка-иконка в строке действий: 40×40, как Button md. */
const ICON_BUTTON = cn(
  "flex items-center justify-center w-10 h-10 rounded-lg border border-line text-ink-3",
  "hover:text-ink-1 hover:bg-surface-hover transition-colors disabled:opacity-50",
);

const PersonForm: React.FC<PersonFormProps> = ({
  initialData,
  onSubmit,
  onChange,
  onRegionChange,
  isLoading = false,
  withCard = true,
  header,
  hideSubmitButton = false,
  submitToolbar,
  submitAddon,
  testPersons,
  onSelectTestPerson,
  onReset,
}) => {
  const [isTestMenuOpen, setIsTestMenuOpen] = useState(false);
  const testMenuRef = useRef<HTMLDivElement>(null);

  // Функция миграции данных: если есть phone_number/email/registration_address, но нет массивов
  const migrateInitialData = (data: PersonData | undefined): PersonData => {
    const base: PersonData = data || { last_name: "", first_name: "", middle_name: "" };

    // Миграция телефона
    let phones = base.phones || [];
    if (phones.length === 0 && base.phone_number) {
      phones = [base.phone_number];
    }
    if (phones.length === 0) {
      phones = [""]; // Начинаем с одного пустого поля
    }

    // Миграция email
    let emails = base.emails || [];
    if (emails.length === 0 && base.email) {
      emails = [base.email];
    }
    if (emails.length === 0) {
      emails = [""]; // Начинаем с одного пустого поля
    }

    // Миграция адресов
    let addresses = base.addresses || [];
    if (addresses.length === 0 && base.registration_address) {
      addresses = [base.registration_address];
    }
    if (addresses.length === 0) {
      addresses = [""]; // Начинаем с одного пустого поля
    }

    return { ...base, phones, emails, addresses };
  };

  const [formData, setFormData] = useState<PersonData>(() => migrateInitialData(initialData));
  const [visibleFields, setVisibleFields] = useState<Set<FieldId>>(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Состояние свёрнутых полей (true = показывать компактный вид)
  const [collapsedFields, setCollapsedFields] = useState<Set<CollapsibleFieldId>>(new Set());
  // Поля в режиме редактирования (временно развёрнуты)
  const [editingFields, setEditingFields] = useState<Set<CollapsibleFieldId>>(new Set());

  // Поле для автофокуса при добавлении или редактировании
  const [fieldToFocus, setFieldToFocus] = useState<CollapsibleFieldId | null>(null);

  // Refs для полей ввода
  const lastNameRef = useRef<HTMLInputElement>(null);
  const birthDateRef = useRef<HTMLInputElement>(null);
  const birthPlaceRef = useRef<HTMLInputElement>(null);
  const passportRef = useRef<HTMLInputElement>(null);
  const driversLicenseRef = useRef<HTMLInputElement>(null);
  const innRef = useRef<HTMLInputElement>(null);
  const snilsRef = useRef<HTMLInputElement>(null);
  const phoneRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  const emailRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  const addressRefs = useRef<Map<number, HTMLInputElement>>(new Map());

  // Кадастровые данные для адресов (по индексу)
  const [addressCadastralData, setAddressCadastralData] = useState<
    Record<
      number,
      {
        stead_cadnum?: string | null;
        house_cadnum?: string | null;
        flat_cadnum?: string | null;
        flat_area?: string | null;
        square_meter_price?: string | null;
        flat_price?: string | null;
      }
    >
  >({});

  // Валидация полей через API
  const {
    validations,
    validatePhone,
    validateEmail,
    validateInn,
    validateSnils,
    validatePassport,
    validateDriversLicense,
    validateName,
    validateAddress,
    clearFieldValidation,
  } = useFieldValidation();

  // Анализ регионов
  const { regionAnalysis, addRegion, removeRegionBySource } = useRegionAnalysis();

  // Закрытие dropdown при клике вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (testMenuRef.current && !testMenuRef.current.contains(event.target as Node)) {
        setIsTestMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Синхронизация с initialData
  useEffect(() => {
    if (initialData) {
      const migratedData = migrateInitialData(initialData);
      setFormData(migratedData);

      // Обновляем visibleFields на основе имеющихся данных
      const newVisibleFields = new Set<FieldId>();
      const newCollapsedFields = new Set<CollapsibleFieldId>();

      // ФИО - сворачиваем если есть полное имя
      const fullName = [migratedData.last_name, migratedData.first_name, migratedData.middle_name]
        .filter(Boolean)
        .join(" ");
      if (fullName.trim().split(/\s+/).length >= 2) {
        newCollapsedFields.add("name");
        // Запускаем валидацию ФИО
        validateName(fullName);
      }

      // Дата рождения
      if (migratedData.birth_date) {
        newVisibleFields.add("birth_date");
        newCollapsedFields.add("birth_date");
      }

      // Место рождения
      if (migratedData.birth_place) {
        newVisibleFields.add("birth_place");
        newCollapsedFields.add("birth_place");
      }

      // Паспорт
      if (migratedData.passport_series?.length === 4 && migratedData.passport_number?.length === 6) {
        newVisibleFields.add("passport");
        newCollapsedFields.add("passport");
        validatePassport(migratedData.passport_series, migratedData.passport_number);
      } else if (migratedData.passport_series || migratedData.passport_number) {
        newVisibleFields.add("passport");
      }

      // Водительское удостоверение
      if (migratedData.drivers_license_series?.length === 4 && migratedData.drivers_license_number?.length === 6) {
        newVisibleFields.add("drivers_license");
        newCollapsedFields.add("drivers_license");
        validateDriversLicense(`${migratedData.drivers_license_series} ${migratedData.drivers_license_number}`);
      } else if (migratedData.drivers_license_series || migratedData.drivers_license_number) {
        newVisibleFields.add("drivers_license");
      }

      // ИНН
      if (migratedData.tax_id?.length === 12) {
        newVisibleFields.add("inn");
        newCollapsedFields.add("inn");
        validateInn(migratedData.tax_id);
      } else if (migratedData.tax_id) {
        newVisibleFields.add("inn");
      }

      // СНИЛС
      const snilsDigits = migratedData.ssn?.replace(/\D/g, "");
      if (snilsDigits?.length === 11) {
        newVisibleFields.add("snils");
        newCollapsedFields.add("snils");
        validateSnils(migratedData.ssn!);
      } else if (migratedData.ssn) {
        newVisibleFields.add("snils");
      }

      // Телефоны
      if (migratedData.phones?.some((p) => p && p.length > 0)) {
        newVisibleFields.add("phone");
        migratedData.phones.forEach((phone, index) => {
          // Форматируем телефон если он в старом формате (только цифры)
          let formattedPhone = phone;
          if (phone && !phone.startsWith("+") && phone.replace(/\D/g, "").length >= 10) {
            const digits = phone.replace(/\D/g, "");
            const normalized = digits.startsWith("8")
              ? "7" + digits.slice(1)
              : digits.startsWith("7")
                ? digits
                : "7" + digits;
            formattedPhone = `+7 (${normalized.slice(1, 4)}) ${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`;
            // Обновляем данные с отформатированным телефоном
            migratedData.phones![index] = formattedPhone;
          }
          if (formattedPhone && formattedPhone.length >= 18) {
            newCollapsedFields.add(`phone_${index}` as CollapsibleFieldId);
            validatePhone(formattedPhone, `phone_${index}`);
          }
        });
        // Обновляем formData с отформатированными телефонами
        setFormData({ ...migratedData });
      }

      // Email
      if (migratedData.emails?.some((e) => e && e.length > 0)) {
        newVisibleFields.add("email");
        migratedData.emails.forEach((email, index) => {
          if (email && email.includes("@")) {
            newCollapsedFields.add(`email_${index}` as CollapsibleFieldId);
            validateEmail(email, `email_${index}`);
          }
        });
      }

      // Адреса
      if (migratedData.addresses?.some((a) => a && a.length > 0)) {
        newVisibleFields.add("address");
        migratedData.addresses.forEach((address, index) => {
          if (address && address.length > 5) {
            newCollapsedFields.add(`address_${index}` as CollapsibleFieldId);
          }
        });
      }

      setVisibleFields(newVisibleFields);
      setCollapsedFields(newCollapsedFields);
      setEditingFields(new Set());
    }
  }, [
    initialData,
    validateName,
    validatePassport,
    validateDriversLicense,
    validateInn,
    validateSnils,
    validatePhone,
    validateEmail,
  ]);

  // Автофокус на поле при добавлении или редактировании
  useEffect(() => {
    if (!fieldToFocus) return;

    // Небольшая задержка чтобы DOM успел обновиться
    const timer = setTimeout(() => {
      let inputElement: HTMLInputElement | null = null;

      if (fieldToFocus === "name") {
        inputElement = lastNameRef.current;
      } else if (fieldToFocus === "birth_date") {
        inputElement = birthDateRef.current;
      } else if (fieldToFocus === "birth_place") {
        inputElement = birthPlaceRef.current;
      } else if (fieldToFocus === "passport") {
        inputElement = passportRef.current;
      } else if (fieldToFocus === "drivers_license") {
        inputElement = driversLicenseRef.current;
      } else if (fieldToFocus === "inn") {
        inputElement = innRef.current;
      } else if (fieldToFocus === "snils") {
        inputElement = snilsRef.current;
      } else if (fieldToFocus.startsWith("phone_")) {
        const index = parseInt(fieldToFocus.split("_")[1], 10);
        inputElement = phoneRefs.current.get(index) || null;
      } else if (fieldToFocus.startsWith("email_")) {
        const index = parseInt(fieldToFocus.split("_")[1], 10);
        inputElement = emailRefs.current.get(index) || null;
      } else if (fieldToFocus.startsWith("address_")) {
        const index = parseInt(fieldToFocus.split("_")[1], 10);
        inputElement = addressRefs.current.get(index) || null;
      }

      if (inputElement) {
        inputElement.focus();
        // Для текстовых полей ставим курсор в конец
        if (inputElement.type === "text" || inputElement.type === "tel" || inputElement.type === "email") {
          const len = inputElement.value.length;
          inputElement.setSelectionRange(len, len);
        }
      }

      setFieldToFocus(null);
    }, 50);

    return () => clearTimeout(timer);
  }, [fieldToFocus]);

  // Автоматическое сворачивание ФИО при успешной валидации
  useEffect(() => {
    if (validations.name?.status === "valid" && !editingFields.has("name")) {
      setCollapsedFields((prev) => new Set([...prev, "name"]));
    }
  }, [validations.name?.status, editingFields]);

  // Автоматическая валидация паспорта когда все данные заполнены
  useEffect(() => {
    const hasAllPassportData =
      formData.passport_series?.length === 4 &&
      formData.passport_number?.length === 6 &&
      formData.passport_issue_date?.length === 10; // DD.MM.YYYY

    // Валидируем только если все данные есть и ещё не валидировали
    if (hasAllPassportData && !validations.passport) {
      validatePassport(formData.passport_series!, formData.passport_number!);
    }
  }, [
    formData.passport_series,
    formData.passport_number,
    formData.passport_issue_date,
    validations.passport,
    validatePassport,
  ]);

  // Автоматическое сворачивание паспорта при завершённой валидации (valid или invalid)
  useEffect(() => {
    const hasAllPassportData = formData.passport_series && formData.passport_number && formData.passport_issue_date;

    const passportValidated = validations.passport?.status === "valid" || validations.passport?.status === "invalid";

    if (passportValidated && !editingFields.has("passport") && hasAllPassportData) {
      setCollapsedFields((prev) => new Set([...prev, "passport"]));
    }
  }, [
    validations.passport?.status,
    editingFields,
    formData.passport_series,
    formData.passport_number,
    formData.passport_issue_date,
  ]);

  // Автоматическая валидация ВУ когда все данные заполнены
  useEffect(() => {
    const hasAllDriversLicenseData =
      formData.drivers_license_series?.length === 4 &&
      formData.drivers_license_number?.length === 6 &&
      formData.drivers_license_issue_date?.length === 10; // DD.MM.YYYY

    // Валидируем только если все данные есть и ещё не валидировали
    if (hasAllDriversLicenseData && !validations.drivers_license) {
      validateDriversLicense(`${formData.drivers_license_series} ${formData.drivers_license_number}`);
    }
  }, [
    formData.drivers_license_series,
    formData.drivers_license_number,
    formData.drivers_license_issue_date,
    validations.drivers_license,
    validateDriversLicense,
  ]);

  // Автоматическое сворачивание ВУ при завершённой валидации (valid или invalid)
  useEffect(() => {
    const hasAllDriversLicenseData =
      formData.drivers_license_series && formData.drivers_license_number && formData.drivers_license_issue_date;

    const driversLicenseValidated =
      validations.drivers_license?.status === "valid" || validations.drivers_license?.status === "invalid";

    if (driversLicenseValidated && !editingFields.has("drivers_license") && hasAllDriversLicenseData) {
      setCollapsedFields((prev) => new Set([...prev, "drivers_license"]));
    }
  }, [
    validations.drivers_license?.status,
    editingFields,
    formData.drivers_license_series,
    formData.drivers_license_number,
    formData.drivers_license_issue_date,
  ]);

  // Автоматическое сворачивание ИНН при завершённой валидации (valid или invalid)
  useEffect(() => {
    const innValidated = validations.inn?.status === "valid" || validations.inn?.status === "invalid";

    if (innValidated && !editingFields.has("inn") && formData.tax_id) {
      setCollapsedFields((prev) => new Set([...prev, "inn"]));
    }
  }, [validations.inn?.status, editingFields, formData.tax_id]);

  // Автоматическое сворачивание СНИЛС при завершённой валидации (valid или invalid)
  useEffect(() => {
    const snilsValidated = validations.snils?.status === "valid" || validations.snils?.status === "invalid";

    if (snilsValidated && !editingFields.has("snils") && formData.ssn) {
      setCollapsedFields((prev) => new Set([...prev, "snils"]));
    }
  }, [validations.snils?.status, editingFields, formData.ssn]);

  // Автоматическое сворачивание телефонов при завершённой валидации (valid или invalid)
  useEffect(() => {
    const phones = formData.phones || [];
    phones.forEach((phone, index) => {
      const fieldKey = `phone_${index}` as const;
      const validation = validations[fieldKey];
      const phoneValidated = validation?.status === "valid" || validation?.status === "invalid";

      if (phoneValidated && !editingFields.has(fieldKey) && phone) {
        setCollapsedFields((prev) => new Set([...prev, fieldKey]));
      }
    });
  }, [validations, editingFields, formData.phones]);

  // Автоматическое сворачивание email при завершённой валидации (valid или invalid)
  useEffect(() => {
    const emails = formData.emails || [];
    emails.forEach((email, index) => {
      const fieldKey = `email_${index}` as const;
      const validation = validations[fieldKey];
      const emailValidated = validation?.status === "valid" || validation?.status === "invalid";

      if (emailValidated && !editingFields.has(fieldKey) && email) {
        setCollapsedFields((prev) => new Set([...prev, fieldKey]));
      }
    });
  }, [validations, editingFields, formData.emails]);

  // Автоматическое сворачивание адресов при завершённой валидации (valid или invalid)
  useEffect(() => {
    const addresses = formData.addresses || [];
    addresses.forEach((address, index) => {
      const fieldKey = `address_${index}` as const;
      const validation = validations[fieldKey];
      const addressValidated = validation?.status === "valid" || validation?.status === "invalid";

      if (addressValidated && !editingFields.has(fieldKey) && address) {
        setCollapsedFields((prev) => new Set([...prev, fieldKey]));
      }
    });
  }, [validations, editingFields, formData.addresses]);

  // Добавление региона из паспорта в анализ регионов
  useEffect(() => {
    if (validations.passport?.status === "valid" && validations.passport?.details?.length) {
      const regionDetail = validations.passport.details.find((d) => d.startsWith("Регион:"));
      if (regionDetail) {
        const regionName = regionDetail.replace("Регион:", "").trim();
        addRegion(regionName, "passport");
      }
    }
  }, [validations.passport?.status, validations.passport?.details, addRegion]);

  // Добавление региона из ИНН в анализ регионов
  useEffect(() => {
    if (validations.inn?.status === "valid" && validations.inn?.details?.length) {
      const regionDetail = validations.inn.details.find((d) => d.startsWith("Регион:"));
      if (regionDetail) {
        const regionName = regionDetail.replace("Регион:", "").trim();
        addRegion(regionName, "tax_id");
      }
    }
  }, [validations.inn?.status, validations.inn?.details, addRegion]);

  // Добавление региона из телефонов в анализ регионов
  useEffect(() => {
    const phones = formData.phones || [];
    phones.forEach((_, index) => {
      const fieldKey = `phone_${index}` as const;
      const validation = validations[fieldKey];
      if (validation?.status === "valid" && validation?.details?.length) {
        const regionDetail = validation.details.find((d) => d.startsWith("Регион:"));
        if (regionDetail) {
          const regionName = regionDetail.replace("Регион:", "").trim();
          addRegion(regionName, `phone_${index}` as `phone_${number}`);
        }
      }
    });
  }, [validations, formData.phones, addRegion]);

  // Уведомляем родительский компонент об изменении regionAnalysis
  useEffect(() => {
    onRegionChange?.(regionAnalysis);
  }, [regionAnalysis, onRegionChange]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Синхронизация первого телефона/email/адреса с одиночными полями для обратной совместимости
    const phones = formData.phones?.filter((p) => p && p.length >= 18) || [];
    const emails = formData.emails?.filter((e) => e && e.includes("@")) || [];
    const addresses = formData.addresses?.filter((a) => a && a.length > 5) || [];

    const submitData: PersonData = {
      ...formData,
      phone_number: phones[0] || formData.phone_number || "",
      email: emails[0] || formData.email || "",
      registration_address: addresses[0] || formData.registration_address || "",
      phones: phones.length > 0 ? phones : undefined,
      emails: emails.length > 0 ? emails : undefined,
      addresses: addresses.length > 0 ? addresses : undefined,
    };

    onSubmit(submitData);
  };

  const handleChange = useCallback(
    (field: keyof PersonData, value: string) => {
      setFormData((prev) => {
        const newData = { ...prev, [field]: value };
        if (onChange) {
          onChange(newData);
        }
        return newData;
      });
    },
    [onChange],
  );

  const addField = (fieldId: FieldId) => {
    setVisibleFields((prev) => new Set([...prev, fieldId]));
    setIsDropdownOpen(false);

    // Устанавливаем фокус на добавленное поле
    if (fieldId === "phone") {
      // Для телефона - фокус на первый несвёрнутый телефон
      const phones = formData.phones || [""];
      const firstExpandedIndex = phones.findIndex(
        (_, index) => !collapsedFields.has(`phone_${index}` as CollapsibleFieldId),
      );
      setFieldToFocus(`phone_${firstExpandedIndex >= 0 ? firstExpandedIndex : 0}` as CollapsibleFieldId);
    } else if (fieldId === "email") {
      // Для email - фокус на первый несвёрнутый email
      const emails = formData.emails || [""];
      const firstExpandedIndex = emails.findIndex(
        (_, index) => !collapsedFields.has(`email_${index}` as CollapsibleFieldId),
      );
      setFieldToFocus(`email_${firstExpandedIndex >= 0 ? firstExpandedIndex : 0}` as CollapsibleFieldId);
    } else if (fieldId === "address") {
      // Для адреса - фокус на первый несвёрнутый адрес
      const addresses = formData.addresses || [""];
      const firstExpandedIndex = addresses.findIndex(
        (_, index) => !collapsedFields.has(`address_${index}` as CollapsibleFieldId),
      );
      setFieldToFocus(`address_${firstExpandedIndex >= 0 ? firstExpandedIndex : 0}` as CollapsibleFieldId);
    } else {
      // Для остальных полей - прямое соответствие
      setFieldToFocus(fieldId as CollapsibleFieldId);
    }
  };

  // Обработчики для массива телефонов
  const handleAddPhone = useCallback(() => {
    setFormData((prev) => {
      const phones = [...(prev.phones || []), ""];
      const newIndex = phones.length - 1;
      // Устанавливаем фокус на новое поле
      setFieldToFocus(`phone_${newIndex}` as CollapsibleFieldId);
      const newData = { ...prev, phones };
      onChange?.(newData);
      return newData;
    });
  }, [onChange]);

  const handleRemovePhone = useCallback(
    (index: number) => {
      const fieldKey = `phone_${index}` as CollapsibleFieldId;
      clearFieldValidation(fieldKey);
      removeRegionBySource(`phone_${index}`);

      setFormData((prev) => {
        const phones = [...(prev.phones || [])];
        phones.splice(index, 1);
        if (phones.length === 0) phones.push("");
        const newData = { ...prev, phones };
        onChange?.(newData);
        return newData;
      });

      // Удаляем из collapsedFields и editingFields
      setCollapsedFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
      setEditingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    },
    [onChange, clearFieldValidation, removeRegionBySource],
  );

  const handlePhoneChange = useCallback(
    (index: number, value: string) => {
      setFormData((prev) => {
        const phones = [...(prev.phones || [])];
        phones[index] = value;
        const newData = { ...prev, phones };
        onChange?.(newData);
        return newData;
      });
    },
    [onChange],
  );

  // Обработчики для массива email
  const handleAddEmail = useCallback(() => {
    setFormData((prev) => {
      const emails = [...(prev.emails || []), ""];
      const newIndex = emails.length - 1;
      // Устанавливаем фокус на новое поле
      setFieldToFocus(`email_${newIndex}` as CollapsibleFieldId);
      const newData = { ...prev, emails };
      onChange?.(newData);
      return newData;
    });
  }, [onChange]);

  const handleRemoveEmail = useCallback(
    (index: number) => {
      const fieldKey = `email_${index}` as CollapsibleFieldId;
      clearFieldValidation(fieldKey);

      setFormData((prev) => {
        const emails = [...(prev.emails || [])];
        emails.splice(index, 1);
        if (emails.length === 0) emails.push("");
        const newData = { ...prev, emails };
        onChange?.(newData);
        return newData;
      });

      // Удаляем из collapsedFields и editingFields
      setCollapsedFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
      setEditingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    },
    [onChange, clearFieldValidation],
  );

  const handleEmailChange = useCallback(
    (index: number, value: string) => {
      setFormData((prev) => {
        const emails = [...(prev.emails || [])];
        emails[index] = value;
        const newData = { ...prev, emails };
        onChange?.(newData);
        return newData;
      });
    },
    [onChange],
  );

  // Обработчики для массива адресов
  const handleAddAddress = useCallback(() => {
    setFormData((prev) => {
      const addresses = [...(prev.addresses || []), ""];
      const newIndex = addresses.length - 1;
      // Устанавливаем фокус на новое поле
      setFieldToFocus(`address_${newIndex}` as CollapsibleFieldId);
      const newData = { ...prev, addresses };
      onChange?.(newData);
      return newData;
    });
  }, [onChange]);

  const handleRemoveAddress = useCallback(
    (index: number) => {
      const fieldKey = `address_${index}` as CollapsibleFieldId;
      removeRegionBySource(`address_${index}`);
      clearFieldValidation(fieldKey);

      setFormData((prev) => {
        const addresses = [...(prev.addresses || [])];
        addresses.splice(index, 1);
        if (addresses.length === 0) addresses.push("");
        const newData = { ...prev, addresses };
        onChange?.(newData);
        return newData;
      });

      // Удаляем из collapsedFields и editingFields
      setCollapsedFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
      setEditingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    },
    [onChange, removeRegionBySource, clearFieldValidation],
  );

  const handleAddressChange = useCallback(
    (index: number, value: string) => {
      setFormData((prev) => {
        const addresses = [...(prev.addresses || [])];
        addresses[index] = value;
        const newData = { ...prev, addresses };
        onChange?.(newData);
        return newData;
      });
    },
    [onChange],
  );

  const handleAddressSelect = useCallback(
    (index: number, data: AddressData) => {
      if (data.region) {
        addRegion(data.region, `address_${index}` as `address_${number}`);
      }

      // Сохраняем кадастровые данные
      const cadastralData = {
        stead_cadnum: data.stead_cadnum,
        house_cadnum: data.house_cadnum,
        flat_cadnum: data.flat_cadnum,
        flat_area: data.flat_area,
        square_meter_price: data.square_meter_price,
        flat_price: data.flat_price,
      };

      setAddressCadastralData((prev) => ({
        ...prev,
        [index]: cadastralData,
      }));

      // Валидируем адрес через Pullenti с кадастровыми данными
      if (data.value) {
        validateAddress(data.value, `address_${index}`, cadastralData);
      }

      // Сворачиваем адрес после выбора и выходим из режима редактирования
      // (если поле было раскрыто кнопкой ✏️). Без этого collapsedAddressIndices
      // отбрасывает индекс из-за `!editingFields.has(...)`.
      const fieldKey = `address_${index}` as CollapsibleFieldId;
      setEditingFields((prev) => {
        if (!prev.has(fieldKey)) return prev;
        const next = new Set(prev);
        next.delete(fieldKey);
        return next;
      });
      setCollapsedFields((prev) => new Set([...prev, fieldKey]));
    },
    [addRegion, validateAddress],
  );

  // Обработчик blur для адресов (сворачивание при ручном вводе)
  const handleAddressBlur = useCallback((index: number, value: string) => {
    if (value && value.length > 5) {
      const fieldKey = `address_${index}` as CollapsibleFieldId;
      setEditingFields((prev) => {
        if (!prev.has(fieldKey)) return prev;
        const next = new Set(prev);
        next.delete(fieldKey);
        return next;
      });
      setCollapsedFields((prev) => new Set([...prev, fieldKey]));
    }
  }, []);

  // Обработчик редактирования свёрнутого поля
  const handleEditField = useCallback((fieldId: CollapsibleFieldId) => {
    setEditingFields((prev) => new Set([...prev, fieldId]));
    setCollapsedFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fieldId);
      return newSet;
    });
    // Устанавливаем фокус на редактируемое поле
    setFieldToFocus(fieldId);
  }, []);

  // Обработчик очистки поля
  const handleClearField = useCallback(
    (fieldId: CollapsibleFieldId) => {
      setCollapsedFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });
      setEditingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });

      if (fieldId === "name") {
        handleChange("last_name", "");
        handleChange("first_name", "");
        handleChange("middle_name", "");
        clearFieldValidation("name");
      } else if (fieldId === "birth_date") {
        handleChange("birth_date", "");
        // Удаляем из visibleFields чтобы можно было добавить снова
        setVisibleFields((prev) => {
          const newSet = new Set(prev);
          newSet.delete("birth_date");
          return newSet;
        });
      } else if (fieldId === "birth_place") {
        handleChange("birth_place", "");
        removeRegionBySource("birth_place");
        // Удаляем из visibleFields чтобы можно было добавить снова
        setVisibleFields((prev) => {
          const newSet = new Set(prev);
          newSet.delete("birth_place");
          return newSet;
        });
      } else if (fieldId === "passport") {
        handleChange("passport_series", "");
        handleChange("passport_number", "");
        handleChange("passport_issue_date", "");
        clearFieldValidation("passport");
        removeRegionBySource("passport");
        // Удаляем из visibleFields чтобы можно было добавить снова
        setVisibleFields((prev) => {
          const newSet = new Set(prev);
          newSet.delete("passport");
          return newSet;
        });
      } else if (fieldId === "drivers_license") {
        handleChange("drivers_license_series", "");
        handleChange("drivers_license_number", "");
        handleChange("drivers_license_issue_date", "");
        clearFieldValidation("drivers_license");
        // Удаляем из visibleFields чтобы можно было добавить снова
        setVisibleFields((prev) => {
          const newSet = new Set(prev);
          newSet.delete("drivers_license");
          return newSet;
        });
      } else if (fieldId === "inn") {
        handleChange("tax_id", "");
        clearFieldValidation("inn");
        removeRegionBySource("tax_id");
        // Удаляем из visibleFields чтобы можно было добавить снова
        setVisibleFields((prev) => {
          const newSet = new Set(prev);
          newSet.delete("inn");
          return newSet;
        });
      } else if (fieldId === "snils") {
        handleChange("ssn", "");
        clearFieldValidation("snils");
        // Удаляем из visibleFields чтобы можно было добавить снова
        setVisibleFields((prev) => {
          const newSet = new Set(prev);
          newSet.delete("snils");
          return newSet;
        });
      } else if (fieldId.startsWith("phone_")) {
        // Обработка indexed phone field (phone_0, phone_1, etc.)
        const index = parseInt(fieldId.split("_")[1], 10);
        handleRemovePhone(index);
      } else if (fieldId.startsWith("email_")) {
        // Обработка indexed email field (email_0, email_1, etc.)
        const index = parseInt(fieldId.split("_")[1], 10);
        handleRemoveEmail(index);
      } else if (fieldId.startsWith("address_")) {
        // Обработка indexed address field (address_0, address_1, etc.)
        const index = parseInt(fieldId.split("_")[1], 10);
        handleRemoveAddress(index);
      }
    },
    [
      handleChange,
      clearFieldValidation,
      removeRegionBySource,
      handleRemovePhone,
      handleRemoveEmail,
      handleRemoveAddress,
    ],
  );

  // Завершение редактирования и сворачивание
  const handleFinishEditing = useCallback(
    (fieldId: CollapsibleFieldId) => {
      setEditingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });

      // Проверяем данные и сворачиваем если заполнены
      if (fieldId === "name") {
        const fullName = [formData.last_name, formData.first_name, formData.middle_name].filter(Boolean).join(" ");
        if (fullName.trim().split(/\s+/).length >= 2) {
          validateName(fullName);
        }
      } else if (fieldId === "birth_date" && formData.birth_date) {
        setCollapsedFields((prev) => new Set([...prev, "birth_date"]));
      } else if (fieldId === "birth_place" && formData.birth_place) {
        setCollapsedFields((prev) => new Set([...prev, "birth_place"]));
      } else if (fieldId === "passport" && formData.passport_series && formData.passport_number) {
        // Запускаем валидацию паспорта, сворачивание произойдёт в useEffect при успешной валидации
        validatePassport(formData.passport_series, formData.passport_number);
      } else if (fieldId === "drivers_license" && formData.drivers_license_series && formData.drivers_license_number) {
        // Запускаем валидацию ВУ, сворачивание произойдёт в useEffect при успешной валидации
        validateDriversLicense(`${formData.drivers_license_series} ${formData.drivers_license_number}`);
      } else if (fieldId === "inn" && formData.tax_id) {
        // Запускаем валидацию ИНН, сворачивание произойдёт в useEffect при успешной валидации
        validateInn(formData.tax_id);
      } else if (fieldId === "snils" && formData.ssn) {
        // Запускаем валидацию СНИЛС, сворачивание произойдёт в useEffect при успешной валидации
        validateSnils(formData.ssn);
      } else if (fieldId.startsWith("phone_")) {
        // Обработка indexed phone field (phone_0, phone_1, etc.)
        const index = parseInt(fieldId.split("_")[1], 10);
        const phone = formData.phones?.[index];
        if (phone && phone.length >= 18) {
          validatePhone(phone, fieldId);
        }
      } else if (fieldId.startsWith("email_")) {
        // Обработка indexed email field (email_0, email_1, etc.)
        const index = parseInt(fieldId.split("_")[1], 10);
        const email = formData.emails?.[index];
        if (email && email.includes("@")) {
          validateEmail(email, fieldId);
        }
      } else if (fieldId.startsWith("address_")) {
        // Обработка indexed address field (address_0, address_1, etc.)
        const index = parseInt(fieldId.split("_")[1], 10);
        const address = formData.addresses?.[index];
        if (address && address.length > 5) {
          // Адрес сворачивается при выборе из подсказок, но если пользователь ввёл вручную - тоже сворачиваем
          setCollapsedFields((prev) => new Set([...prev, fieldId as CollapsibleFieldId]));
        }
      }
    },
    [
      formData,
      validateName,
      validatePassport,
      validateDriversLicense,
      validateInn,
      validateSnils,
      validatePhone,
      validateEmail,
    ],
  );

  // Вычисляем полное имя
  const fullName = useMemo(() => {
    return [formData.last_name, formData.first_name, formData.middle_name].filter(Boolean).join(" ");
  }, [formData.last_name, formData.first_name, formData.middle_name]);

  // Форматированная дата рождения с возрастом
  const formattedBirthDate = useMemo(() => {
    if (!formData.birth_date) return null;
    return formatDateWithAge(formData.birth_date);
  }, [formData.birth_date]);

  // Проверка: показывать ли свёрнутый вид для полей
  const isNameCollapsed = collapsedFields.has("name") && !editingFields.has("name") && fullName.trim();
  const isBirthDateCollapsed =
    collapsedFields.has("birth_date") && !editingFields.has("birth_date") && formData.birth_date;
  const isBirthPlaceCollapsed =
    collapsedFields.has("birth_place") && !editingFields.has("birth_place") && formData.birth_place;
  const isPassportCollapsed =
    collapsedFields.has("passport") &&
    !editingFields.has("passport") &&
    formData.passport_series &&
    formData.passport_number &&
    formData.passport_issue_date;
  const isDriversLicenseCollapsed =
    collapsedFields.has("drivers_license") &&
    !editingFields.has("drivers_license") &&
    formData.drivers_license_series &&
    formData.drivers_license_number &&
    formData.drivers_license_issue_date;
  const isInnCollapsed = collapsedFields.has("inn") && !editingFields.has("inn") && formData.tax_id;
  const isSnilsCollapsed = collapsedFields.has("snils") && !editingFields.has("snils") && formData.ssn;

  // Проверяем, есть ли свёрнутые телефоны
  const collapsedPhoneIndices = useMemo(() => {
    const phones = formData.phones || [];
    return phones
      .map((phone, index) => {
        const fieldKey = `phone_${index}` as CollapsibleFieldId;
        const isCollapsed = collapsedFields.has(fieldKey) && !editingFields.has(fieldKey) && phone;
        return isCollapsed ? index : -1;
      })
      .filter((i) => i >= 0);
  }, [formData.phones, collapsedFields, editingFields]);

  // Проверяем, есть ли свёрнутые email
  const collapsedEmailIndices = useMemo(() => {
    const emails = formData.emails || [];
    return emails
      .map((email, index) => {
        const fieldKey = `email_${index}` as CollapsibleFieldId;
        const isCollapsed = collapsedFields.has(fieldKey) && !editingFields.has(fieldKey) && email;
        return isCollapsed ? index : -1;
      })
      .filter((i) => i >= 0);
  }, [formData.emails, collapsedFields, editingFields]);

  // Проверяем, есть ли свёрнутые адреса
  const collapsedAddressIndices = useMemo(() => {
    const addresses = formData.addresses || [];
    return addresses
      .map((address, index) => {
        const fieldKey = `address_${index}` as CollapsibleFieldId;
        const isCollapsed = collapsedFields.has(fieldKey) && !editingFields.has(fieldKey) && address;
        return isCollapsed ? index : -1;
      })
      .filter((i) => i >= 0);
  }, [formData.addresses, collapsedFields, editingFields]);

  // Собираем данные для таблицы свёрнутых полей
  const collapsedFieldsData = useMemo(() => {
    const fields: CollapsedFieldData[] = [];

    if (isNameCollapsed) {
      fields.push({
        id: "name",
        label: "Полное имя",
        value: fullName,
      });
    }

    if (isBirthDateCollapsed && formattedBirthDate) {
      fields.push({
        id: "birth_date",
        label: "Дата рождения",
        value: formattedBirthDate.date,
        suffix: formattedBirthDate.age || undefined,
        suffixIcon: formattedBirthDate.age ? <Cake className="w-4 h-4" /> : undefined,
      });
    }

    if (isBirthPlaceCollapsed && formData.birth_place) {
      fields.push({
        id: "birth_place",
        label: "Место рождения",
        value: formData.birth_place,
      });
    }

    return fields;
  }, [
    isNameCollapsed,
    fullName,
    isBirthDateCollapsed,
    formattedBirthDate,
    isBirthPlaceCollapsed,
    formData.birth_place,
  ]);

  // Форматированные данные паспорта для свёрнутого вида
  const formattedPassport = useMemo(() => {
    if (!formData.passport_series || !formData.passport_number) return null;

    const passportNumber = `${formData.passport_series} ${formData.passport_number}`;
    const mainText = `Паспорт гражданина РФ ${passportNumber}`;

    // Формируем строку выдачи
    let issueText = "";
    if (formData.passport_issue_date) {
      // Добавляем регион из валидации если есть
      if (validations.passport?.details?.length) {
        const regionDetail = validations.passport.details.find((d) => d.startsWith("Регион:"));
        if (regionDetail) {
          const regionName = regionDetail.replace("Регион:", "").trim();
          issueText = `${regionName}. `;
        }
      }
      // Дата уже в формате DD.MM.YYYY
      issueText += `Выдан ${formData.passport_issue_date}`;
    }

    return { mainText, issueText };
  }, [formData.passport_series, formData.passport_number, formData.passport_issue_date, validations.passport]);

  // Форматированные данные ВУ для свёрнутого вида
  const formattedDriversLicense = useMemo(() => {
    if (!formData.drivers_license_series || !formData.drivers_license_number) return null;

    // Форматируем серию с пробелом: XXYY -> XX YY
    const series = formData.drivers_license_series;
    const formattedSeries = series.length === 4 ? `${series.slice(0, 2)} ${series.slice(2, 4)}` : series;
    const licenseNumber = `${formattedSeries} ${formData.drivers_license_number}`;
    const mainText = `Водительское удостоверение ${licenseNumber}`;

    // Формируем строку выдачи
    let issueText = "";
    if (formData.drivers_license_issue_date) {
      // Добавляем информацию о формате из валидации если есть
      if (validations.drivers_license?.details?.length) {
        const formatDetail = validations.drivers_license.details.find((d) => d.startsWith("Серия:"));
        if (formatDetail) {
          const formatInfo = formatDetail.replace("Серия:", "").trim();
          issueText = `${formatInfo}. `;
        }
      }
      // Дата уже в формате DD.MM.YYYY
      issueText += `Выдано ${formData.drivers_license_issue_date}`;
    }

    return { mainText, issueText };
  }, [
    formData.drivers_license_series,
    formData.drivers_license_number,
    formData.drivers_license_issue_date,
    validations.drivers_license,
  ]);

  // Форматированные данные ИНН для свёрнутого вида
  const formattedInn = useMemo(() => {
    if (!formData.tax_id) return null;

    const mainText = `ИНН ${formData.tax_id}`;

    // Формируем строку с регионом из валидации
    let regionText = "";
    if (validations.inn?.details?.length) {
      const regionDetail = validations.inn.details.find((d) => d.startsWith("Регион:"));
      if (regionDetail) {
        regionText = regionDetail.replace("Регион:", "").trim();
      }
    }

    return { mainText, regionText };
  }, [formData.tax_id, validations.inn]);

  // Форматированные данные СНИЛС для свёрнутого вида
  const formattedSnils = useMemo(() => {
    if (!formData.ssn) return null;

    // Форматируем СНИЛС: XXX-XXX-XXX XX
    const digits = formData.ssn.replace(/\D/g, "");
    let formatted = digits;
    if (digits.length === 11) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)} ${digits.slice(9, 11)}`;
    }

    const mainText = `СНИЛС ${formatted}`;

    return { mainText };
  }, [formData.ssn]);

  // Форматированные данные телефонов для свёрнутого вида
  const formattedPhones = useMemo(() => {
    const phones = formData.phones || [];
    return phones.map((phone, index) => {
      if (!phone) return null;

      const validation = validations[`phone_${index}`];
      // Используем formatted_8 из валидации если есть, иначе форматируем вручную
      let mainText = validation?.formattedValue || phone;
      if (!validation?.formattedValue) {
        const digits = phone.replace(/\D/g, "");
        if (digits.length === 11) {
          const normalized = digits.startsWith("7") ? "8" + digits.slice(1) : digits;
          mainText = `${normalized.slice(0, 1)}(${normalized.slice(1, 4)})${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`;
        }
      }

      // Формируем строку с информацией из валидации (оператор, регион)
      let detailsText = "";
      if (validation?.details?.length) {
        detailsText = validation.details.join(". ");
      }

      return { mainText, detailsText, index };
    });
  }, [formData.phones, validations]);

  // Форматированные данные email для свёрнутого вида
  const formattedEmails = useMemo(() => {
    const emails = formData.emails || [];
    return emails.map((email, index) => {
      if (!email) return null;

      const mainText = email;
      const validation = validations[`email_${index}`];

      // Формируем строку с информацией из валидации (тип домена)
      let detailsText = "";
      if (validation?.details?.length) {
        detailsText = validation.details.join(". ");
      }

      return { mainText, detailsText, index };
    });
  }, [formData.emails, validations]);

  // Форматированные данные адресов для свёрнутого вида
  const formattedAddresses = useMemo(() => {
    const addresses = formData.addresses || [];
    return addresses.map((address, index) => {
      if (!address) return null;

      const mainText = address;
      const cadastral = addressCadastralData[index];

      // Формируем дополнительную информацию
      const details: string[] = [];

      // Кадастровый номер (приоритет: flat > house > stead)
      const cadNum = cadastral?.flat_cadnum || cadastral?.house_cadnum || cadastral?.stead_cadnum;
      if (cadNum) {
        details.push(`Кадастровый №: ${cadNum}`);
      }

      // Площадь
      if (cadastral?.flat_area) {
        details.push(`Площадь: ${cadastral.flat_area} м²`);
      }

      // Стоимость
      if (cadastral?.flat_price) {
        const price = parseFloat(cadastral.flat_price);
        if (!isNaN(price)) {
          const formattedPrice = new Intl.NumberFormat("ru-RU").format(price);
          details.push(`Стоимость: ${formattedPrice} руб.`);
        }
      }

      return {
        mainText,
        detailsText: details.length > 0 ? details.join(" • ") : undefined,
        index,
      };
    });
  }, [formData.addresses, addressCadastralData]);

  // Собираем данные для таблицы свёрнутых паспортов
  const collapsedPassportsData = useMemo(() => {
    const fields: CollapsedFieldData[] = [];

    if (isPassportCollapsed && formattedPassport) {
      fields.push({
        id: "passport",
        label: formattedPassport.mainText,
        value: formattedPassport.issueText || "",
        layout: "vertical",
        validationStatus:
          validations.passport?.status === "valid"
            ? "valid"
            : validations.passport?.status === "invalid"
              ? "invalid"
              : undefined,
      });
    }

    return fields;
  }, [isPassportCollapsed, formattedPassport, validations.passport?.status]);

  // Собираем данные для таблицы свёрнутых водительских удостоверений
  const collapsedDriversLicensesData = useMemo(() => {
    const fields: CollapsedFieldData[] = [];

    if (isDriversLicenseCollapsed && formattedDriversLicense) {
      fields.push({
        id: "drivers_license",
        label: formattedDriversLicense.mainText,
        value: formattedDriversLicense.issueText || "",
        layout: "vertical",
        validationStatus:
          validations.drivers_license?.status === "valid"
            ? "valid"
            : validations.drivers_license?.status === "invalid"
              ? "invalid"
              : undefined,
      });
    }

    return fields;
  }, [isDriversLicenseCollapsed, formattedDriversLicense, validations.drivers_license?.status]);

  // Собираем данные для таблицы свёрнутых персональных кодов (ИНН и СНИЛС)
  const collapsedCodesData = useMemo(() => {
    const fields: CollapsedFieldData[] = [];

    if (isInnCollapsed && formattedInn) {
      fields.push({
        id: "inn",
        label: formattedInn.mainText,
        value: formattedInn.regionText || "",
        layout: "vertical",
        validationStatus:
          validations.inn?.status === "valid" ? "valid" : validations.inn?.status === "invalid" ? "invalid" : undefined,
      });
    }

    if (isSnilsCollapsed && formattedSnils) {
      fields.push({
        id: "snils",
        label: formattedSnils.mainText,
        value: "",
        layout: "vertical",
        validationStatus:
          validations.snils?.status === "valid"
            ? "valid"
            : validations.snils?.status === "invalid"
              ? "invalid"
              : undefined,
      });
    }

    return fields;
  }, [
    isInnCollapsed,
    formattedInn,
    validations.inn?.status,
    isSnilsCollapsed,
    formattedSnils,
    validations.snils?.status,
  ]);

  // Собираем данные для таблицы свёрнутых телефонов
  const collapsedPhonesData = useMemo(() => {
    const fields: CollapsedFieldData[] = [];

    collapsedPhoneIndices.forEach((index) => {
      const formatted = formattedPhones[index];
      if (formatted) {
        const fieldKey = `phone_${index}`;
        const validation = validations[fieldKey];
        fields.push({
          id: fieldKey,
          label: formatted.mainText,
          value: formatted.detailsText || "",
          layout: "vertical",
          validationStatus:
            validation?.status === "valid" ? "valid" : validation?.status === "invalid" ? "invalid" : undefined,
        });
      }
    });

    return fields;
  }, [collapsedPhoneIndices, formattedPhones, validations]);

  // Собираем данные для таблицы свёрнутых email
  const collapsedEmailsData = useMemo(() => {
    const fields: CollapsedFieldData[] = [];

    collapsedEmailIndices.forEach((index) => {
      const formatted = formattedEmails[index];
      if (formatted) {
        const fieldKey = `email_${index}`;
        const validation = validations[fieldKey];
        fields.push({
          id: fieldKey,
          label: formatted.mainText,
          value: formatted.detailsText || "",
          layout: "vertical",
          validationStatus:
            validation?.status === "valid" ? "valid" : validation?.status === "invalid" ? "invalid" : undefined,
        });
      }
    });

    return fields;
  }, [collapsedEmailIndices, formattedEmails, validations]);

  // Собираем данные для таблицы свёрнутых адресов
  const collapsedAddressesData = useMemo(() => {
    const fields: CollapsedFieldData[] = [];

    collapsedAddressIndices.forEach((index) => {
      const formatted = formattedAddresses[index];
      if (formatted) {
        const fieldKey = `address_${index}`;
        const validation = validations[fieldKey];
        fields.push({
          id: fieldKey,
          label: formatted.mainText,
          value: formatted.detailsText || "",
          layout: "vertical",
          validationStatus:
            validation?.status === "valid"
              ? "valid"
              : validation?.status === "invalid"
                ? "invalid"
                : validation?.status === "validating"
                  ? undefined
                  : "valid", // По умолчанию считаем валидным если нет статуса
        });
      }
    });

    return fields;
  }, [collapsedAddressIndices, formattedAddresses, validations]);

  // Есть ли свёрнутые паспорта
  const hasCollapsedPassports = collapsedPassportsData.length > 0;

  // Есть ли свёрнутые ВУ
  const hasCollapsedDriversLicenses = collapsedDriversLicensesData.length > 0;

  // Есть ли свёрнутые персональные коды (ИНН/СНИЛС)
  const hasCollapsedCodes = collapsedCodesData.length > 0;

  // Есть ли свёрнутые телефоны
  const hasCollapsedPhones = collapsedPhonesData.length > 0;

  // Есть ли свёрнутые email
  const hasCollapsedEmails = collapsedEmailsData.length > 0;

  // Есть ли свёрнутые адреса
  const hasCollapsedAddresses = collapsedAddressesData.length > 0;

  // Есть ли хоть одно свёрнутое поле для показа таблицы
  const hasCollapsedFields = collapsedFieldsData.length > 0;

  // Есть ли поля в режиме редактирования (не свёрнуты)
  const hasExpandedNameField = !isNameCollapsed;
  const hasExpandedBirthDateField = visibleFields.has("birth_date") && !isBirthDateCollapsed;
  const hasExpandedBirthPlaceField = visibleFields.has("birth_place") && !isBirthPlaceCollapsed;
  const hasExpandedPassportField = visibleFields.has("passport") && !isPassportCollapsed;
  const hasExpandedDriversLicenseField = visibleFields.has("drivers_license") && !isDriversLicenseCollapsed;
  const hasExpandedInnField = visibleFields.has("inn") && !isInnCollapsed;
  const hasExpandedSnilsField = visibleFields.has("snils") && !isSnilsCollapsed;

  const availableFields = fieldOptions.filter((f) => !visibleFields.has(f.id));

  // Проверяем видимость аккордеонов
  const hasDocumentFields =
    hasCollapsedPassports ||
    hasCollapsedDriversLicenses ||
    hasCollapsedCodes ||
    visibleFields.has("passport") ||
    visibleFields.has("drivers_license") ||
    visibleFields.has("inn") ||
    visibleFields.has("snils");
  const hasContactFields =
    hasCollapsedPhones ||
    hasCollapsedEmails ||
    hasCollapsedAddresses ||
    visibleFields.has("phone") ||
    visibleFields.has("email") ||
    visibleFields.has("address");

  const formContent = (
    <>
      {header && <div className="pb-4 -mx-6 px-6 border-b border-line">{header}</div>}

      <form onSubmit={handleSubmit}>
        {/* Физическое лицо */}
        <>
          {/* Установочные данные - всегда видно */}
          <AccordionSection label="Установочные данные" defaultOpen noBorder>
            {/* ФИО - развёрнутый вид (всегда первым, если не свёрнуто) */}
            {hasExpandedNameField && (
              <div
                onBlur={(e) => {
                  // Проверяем, не переходит ли фокус на элемент внутри этого же контейнера
                  const relatedTarget = e.relatedTarget as Node | null;
                  if (relatedTarget && e.currentTarget.contains(relatedTarget)) {
                    return;
                  }
                  // Фокус ушел за пределы контейнера - сворачиваем
                  handleFinishEditing("name");
                }}
              >
                <FieldGroup label="Полное имя" hint="Введите ФИО для поиска">
                  <Input
                    ref={lastNameRef}
                    value={formData.last_name || ""}
                    onChange={(e) => handleChange("last_name", e.target.value)}
                    placeholder="Фамилия"
                  />
                  <Input
                    value={formData.first_name || ""}
                    onChange={(e) => handleChange("first_name", e.target.value)}
                    placeholder="Имя"
                  />
                  <Input
                    value={formData.middle_name || ""}
                    onChange={(e) => handleChange("middle_name", e.target.value)}
                    placeholder="Отчество"
                  />
                </FieldGroup>
              </div>
            )}

            {/* Таблица свёрнутых полей (после ФИО) */}
            {hasCollapsedFields && (
              <CollapsedFieldsTable
                fields={collapsedFieldsData}
                onEdit={(fieldId) => handleEditField(fieldId as CollapsibleFieldId)}
                onClear={(fieldId) => handleClearField(fieldId as CollapsibleFieldId)}
              />
            )}

            {/* Дата рождения - развёрнутый вид */}
            {hasExpandedBirthDateField && (
              <FieldGroup label="Дата рождения" onRemove={() => handleClearField("birth_date")}>
                <DateInput
                  ref={birthDateRef}
                  value={formData.birth_date || ""}
                  onChange={(value) => handleChange("birth_date", value)}
                  onBlur={() => {
                    if (formData.birth_date) {
                      handleFinishEditing("birth_date");
                    }
                  }}
                />
              </FieldGroup>
            )}

            {/* Место рождения - развёрнутый вид */}
            {hasExpandedBirthPlaceField && (
              <FieldGroup label="Место рождения" onRemove={() => handleClearField("birth_place")}>
                <AddressSuggest
                  ref={birthPlaceRef}
                  value={formData.birth_place || ""}
                  onChange={(value) => handleChange("birth_place", value)}
                  onSelect={(data: AddressData) => {
                    if (data.region) {
                      addRegion(data.region, "birth_place");
                    }
                  }}
                  onBlur={() => {
                    if (formData.birth_place) {
                      handleFinishEditing("birth_place");
                    }
                  }}
                  placeholder="Начните вводить город..."
                  toSettlement
                />
              </FieldGroup>
            )}
          </AccordionSection>

          {/* Коды и документы */}
          {hasDocumentFields && (
            <AccordionSection label="Коды и документы" defaultOpen className="mt-6">
              {/* Паспорта */}
              {(hasCollapsedPassports || hasExpandedPassportField) && (
                <FieldGroup label="Паспорта">
                  <div className="space-y-3">
                    {/* Свёрнутые паспорта */}
                    {hasCollapsedPassports && (
                      <CollapsedFieldsTable
                        fields={collapsedPassportsData}
                        onEdit={(id) => handleEditField(id as CollapsibleFieldId)}
                        onClear={(id) => handleClearField(id as CollapsibleFieldId)}
                      />
                    )}
                    {/* Развёрнутый паспорт */}
                    {hasExpandedPassportField && (
                      <div
                        onBlur={(e) => {
                          const relatedTarget = e.relatedTarget as Node | null;
                          if (relatedTarget && e.currentTarget.contains(relatedTarget)) {
                            return;
                          }
                          if (formData.passport_series && formData.passport_number) {
                            handleFinishEditing("passport");
                          }
                        }}
                      >
                        <div className="grid grid-cols-3 gap-3">
                          <div className="col-span-2">
                            <PassportInput
                              ref={passportRef}
                              value={
                                formData.passport_series && formData.passport_number
                                  ? `${formData.passport_series} ${formData.passport_number}`
                                  : ""
                              }
                              onChange={(value) => {
                                const digits = value.replace(/\D/g, "");
                                const series = digits.slice(0, 4);
                                const number = digits.slice(4, 10);
                                handleChange("passport_series", series);
                                handleChange("passport_number", number);
                              }}
                              placeholder="0000 000000"
                            />
                          </div>
                          <DateInput
                            value={formData.passport_issue_date || ""}
                            onChange={(value) => handleChange("passport_issue_date", value)}
                            placeholder="Дата выдачи"
                          />
                        </div>
                        {validations.passport && validations.passport.status !== "valid" && (
                          <ValidationIndicator
                            status={validations.passport.status}
                            message={validations.passport.message}
                            details={validations.passport.details}
                            className="mt-1"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </FieldGroup>
              )}

              {/* Водительские удостоверения */}
              {(hasCollapsedDriversLicenses || hasExpandedDriversLicenseField) && (
                <FieldGroup label="Водительские удостоверения">
                  <div className="space-y-3">
                    {/* Свёрнутые ВУ */}
                    {hasCollapsedDriversLicenses && (
                      <CollapsedFieldsTable
                        fields={collapsedDriversLicensesData}
                        onEdit={(id) => handleEditField(id as CollapsibleFieldId)}
                        onClear={(id) => handleClearField(id as CollapsibleFieldId)}
                      />
                    )}
                    {/* Развёрнутое ВУ */}
                    {hasExpandedDriversLicenseField && (
                      <div
                        onBlur={(e) => {
                          const relatedTarget = e.relatedTarget as Node | null;
                          if (relatedTarget && e.currentTarget.contains(relatedTarget)) {
                            return;
                          }
                          if (formData.drivers_license_series && formData.drivers_license_number) {
                            handleFinishEditing("drivers_license");
                          }
                        }}
                      >
                        <div className="grid grid-cols-3 gap-3">
                          <div className="col-span-2">
                            <DriversLicenseInput
                              ref={driversLicenseRef}
                              value={
                                formData.drivers_license_series && formData.drivers_license_number
                                  ? `${formData.drivers_license_series}${formData.drivers_license_number}`
                                  : ""
                              }
                              onChange={(value) => {
                                // Убираем пробелы - они только для отображения
                                const cleaned = value.replace(/\s+/g, "");
                                // Первые 4 символа - серия, последние 6 - номер
                                const series = cleaned.slice(0, 4);
                                const number = cleaned.slice(4, 10);
                                handleChange("drivers_license_series", series);
                                handleChange("drivers_license_number", number);
                              }}
                              placeholder="00 00 000000"
                            />
                          </div>
                          <DateInput
                            value={formData.drivers_license_issue_date || ""}
                            onChange={(value) => handleChange("drivers_license_issue_date", value)}
                            placeholder="Дата выдачи"
                          />
                        </div>
                        {validations.drivers_license && validations.drivers_license.status !== "valid" && (
                          <ValidationIndicator
                            status={validations.drivers_license.status}
                            message={validations.drivers_license.message}
                            details={validations.drivers_license.details}
                            className="mt-1"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </FieldGroup>
              )}

              {/* Персональные коды (ИНН и СНИЛС) */}
              {(hasCollapsedCodes || hasExpandedInnField || hasExpandedSnilsField) && (
                <FieldGroup label="Персональные коды">
                  <div className="space-y-3">
                    {/* Свёрнутые коды */}
                    {hasCollapsedCodes && (
                      <CollapsedFieldsTable
                        fields={collapsedCodesData}
                        onEdit={(id) => handleEditField(id as CollapsibleFieldId)}
                        onClear={(id) => handleClearField(id as CollapsibleFieldId)}
                      />
                    )}
                    {/* Развёрнутый ИНН */}
                    {hasExpandedInnField && (
                      <div
                        onBlur={(e) => {
                          const relatedTarget = e.relatedTarget as Node | null;
                          if (relatedTarget && e.currentTarget.contains(relatedTarget)) {
                            return;
                          }
                          if (formData.tax_id) {
                            handleFinishEditing("inn");
                          }
                        }}
                      >
                        <InnInput
                          ref={innRef}
                          value={formData.tax_id || ""}
                          onChange={(value) => handleChange("tax_id", value)}
                          onBlurValidate={validateInn}
                          type="individual"
                        />
                        {validations.inn && validations.inn.status !== "valid" && (
                          <ValidationIndicator
                            status={validations.inn.status}
                            message={validations.inn.message}
                            details={validations.inn.details}
                            className="mt-1"
                          />
                        )}
                      </div>
                    )}
                    {/* Развёрнутый СНИЛС */}
                    {hasExpandedSnilsField && (
                      <div
                        onBlur={(e) => {
                          const relatedTarget = e.relatedTarget as Node | null;
                          if (relatedTarget && e.currentTarget.contains(relatedTarget)) {
                            return;
                          }
                          if (formData.ssn) {
                            handleFinishEditing("snils");
                          }
                        }}
                      >
                        <SnilsInput
                          ref={snilsRef}
                          value={formData.ssn || ""}
                          onChange={(value) => handleChange("ssn", value)}
                          onBlurValidate={validateSnils}
                        />
                        {validations.snils && validations.snils.status !== "valid" && (
                          <ValidationIndicator
                            status={validations.snils.status}
                            message={validations.snils.message}
                            details={validations.snils.details}
                            className="mt-1"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </FieldGroup>
              )}
            </AccordionSection>
          )}

          {/* Контакты */}
          {hasContactFields && (
            <AccordionSection label="Контакты" defaultOpen className="mt-6">
              {/* Телефоны */}
              {(hasCollapsedPhones || visibleFields.has("phone")) && (
                <FieldGroup label="Телефонные номера">
                  <div className="space-y-3">
                    {/* Свёрнутые телефоны */}
                    {hasCollapsedPhones && (
                      <CollapsedFieldsTable
                        fields={collapsedPhonesData}
                        onEdit={(id) => handleEditField(id as CollapsibleFieldId)}
                        onClear={(id) => handleClearField(id as CollapsibleFieldId)}
                      />
                    )}
                    {/* Развёрнутые телефоны */}
                    {visibleFields.has("phone") && (
                      <MultiPhoneInput
                        values={formData.phones || [""]}
                        onChange={handlePhoneChange}
                        onAdd={handleAddPhone}
                        onRemove={handleRemovePhone}
                        onBlurValidate={validatePhone}
                        validations={validations}
                        collapsedIndices={collapsedPhoneIndices}
                        maxItems={MAX_PHONES}
                        onRegisterRef={(index, el) => {
                          if (el) {
                            phoneRefs.current.set(index, el);
                          } else {
                            phoneRefs.current.delete(index);
                          }
                        }}
                      />
                    )}
                  </div>
                </FieldGroup>
              )}

              {/* Email */}
              {(hasCollapsedEmails || visibleFields.has("email")) && (
                <FieldGroup label="Адреса электронной почты">
                  <div className="space-y-3">
                    {/* Свёрнутые email */}
                    {hasCollapsedEmails && (
                      <CollapsedFieldsTable
                        fields={collapsedEmailsData}
                        onEdit={(id) => handleEditField(id as CollapsibleFieldId)}
                        onClear={(id) => handleClearField(id as CollapsibleFieldId)}
                      />
                    )}
                    {/* Развёрнутые email */}
                    {visibleFields.has("email") && (
                      <MultiEmailInput
                        values={formData.emails || [""]}
                        onChange={handleEmailChange}
                        onAdd={handleAddEmail}
                        onRemove={handleRemoveEmail}
                        onBlurValidate={validateEmail}
                        validations={validations}
                        collapsedIndices={collapsedEmailIndices}
                        maxItems={MAX_EMAILS}
                        onRegisterRef={(index, el) => {
                          if (el) {
                            emailRefs.current.set(index, el);
                          } else {
                            emailRefs.current.delete(index);
                          }
                        }}
                      />
                    )}
                  </div>
                </FieldGroup>
              )}

              {/* Адреса */}
              {(hasCollapsedAddresses || visibleFields.has("address")) && (
                <FieldGroup label="Адреса">
                  <div className="space-y-3">
                    {/* Свёрнутые адреса */}
                    {hasCollapsedAddresses && (
                      <CollapsedFieldsTable
                        fields={collapsedAddressesData}
                        onEdit={(id) => handleEditField(id as CollapsibleFieldId)}
                        onClear={(id) => handleClearField(id as CollapsibleFieldId)}
                      />
                    )}
                    {/* Развёрнутые адреса */}
                    {visibleFields.has("address") && (
                      <MultiAddressInput
                        values={formData.addresses || [""]}
                        onChange={handleAddressChange}
                        onAdd={handleAddAddress}
                        onRemove={handleRemoveAddress}
                        onSelect={handleAddressSelect}
                        onBlur={handleAddressBlur}
                        validations={validations}
                        collapsedIndices={collapsedAddressIndices}
                        maxItems={MAX_ADDRESSES}
                        onRegisterRef={(index, el) => {
                          if (el) {
                            addressRefs.current.set(index, el);
                          } else {
                            addressRefs.current.delete(index);
                          }
                        }}
                      />
                    )}
                  </div>
                </FieldGroup>
              )}
            </AccordionSection>
          )}
        </>

        {/* Чипы добавления данных — приоритетные как dashed pills, остальные в dropdown */}
        {availableFields.length > 0 &&
          (() => {
            const PRIORITY: FieldId[] = ["birth_date", "passport", "inn", "phone", "email"];
            const primary = availableFields.filter((f) => PRIORITY.includes(f.id));
            const more = availableFields.filter((f) => !PRIORITY.includes(f.id));
            return (
              <div className="mt-6">
                <div className="flex flex-wrap items-center gap-1 -ml-2">
                  {primary.map((field) => (
                    <button
                      key={field.id}
                      type="button"
                      onClick={() => addField(field.id)}
                      className={ADD_FIELD_BUTTON}
                    >
                      <Plus size={14} strokeWidth={1.75} className="text-ink-3 shrink-0" />
                      {field.label}
                    </button>
                  ))}
                  {more.length > 0 && (
                    <div className="relative" ref={dropdownRef}>
                      <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className={ADD_FIELD_BUTTON}
                        aria-expanded={isDropdownOpen}
                      >
                        Ещё
                        <ChevronDown size={14} strokeWidth={1.75} className="text-ink-3 shrink-0" />
                      </button>
                      <AnimatePresence>
                        {isDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.12 }}
                            className={cn(
                              "absolute left-0 mt-2 w-60 z-50 py-1.5",
                              "bg-surface-elevated border border-line rounded-lg shadow-shell-md",
                            )}
                          >
                            {more.map((field) => (
                              <button
                                key={field.id}
                                type="button"
                                onClick={() => {
                                  addField(field.id);
                                  setIsDropdownOpen(false);
                                }}
                                className={cn(
                                  "w-full flex items-center gap-2 text-left px-3 py-2 text-fs-sm text-ink-2",
                                  "hover:bg-surface-hover hover:text-ink-1 transition-colors",
                                )}
                              >
                                <CirclePlus size={14} strokeWidth={1.75} className="text-ink-3" />
                                {field.label}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

        {/* Кнопки управления и отправки */}
        {!hideSubmitButton && (
          <>
            {submitToolbar && <div className="mt-6">{submitToolbar}</div>}
            <div
              className={cn(
                "flex flex-wrap items-center justify-end gap-2",
                submitToolbar ? "mt-4 pt-4 border-t border-line" : "mt-6",
              )}
            >
              {submitAddon && <div className="w-full sm:mr-2 sm:w-auto">{submitAddon}</div>}
              {/* Кнопка отправки */}
              <Button type="submit" disabled={isLoading} loading={isLoading}>
                {isLoading ? "Выполняется анализ..." : "Начать проверку"}
              </Button>

              {/* Выпадающее меню тестовых данных */}
              {testPersons && testPersons.length > 0 && onSelectTestPerson && (
                <div className="relative" ref={testMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsTestMenuOpen(!isTestMenuOpen)}
                    className={ICON_BUTTON}
                    disabled={isLoading}
                    title="Тестовые данные"
                  >
                    <Users size={18} strokeWidth={1.75} />
                  </button>

                  <AnimatePresence>
                    {isTestMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 bottom-full mb-2 w-64 bg-surface-elevated rounded-lg shadow-lg border border-line z-50 max-h-80 overflow-y-auto"
                      >
                        <div className="p-2">
                          <div className="px-3 py-2 text-xs font-semibold text-ink-3 uppercase">
                            Выберите тестовую персону
                          </div>
                          {testPersons.map((person, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                onSelectTestPerson(person);
                                setIsTestMenuOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 rounded-md hover:bg-surface-hover transition-colors"
                            >
                              <div className="font-medium text-ink-1 text-sm">{person.label}</div>
                              <div className="text-xs text-ink-3">{person.description}</div>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Кнопка сброса */}
              {onReset && (
                <button
                  type="button"
                  onClick={onReset}
                  className={ICON_BUTTON}
                  disabled={isLoading}
                  title="Сбросить форму"
                >
                  <RotateCcw size={18} strokeWidth={1.75} />
                </button>
              )}
            </div>
          </>
        )}
      </form>
    </>
  );

  if (!withCard) {
    return <div>{formContent}</div>;
  }

  return <Card padding="lg">{formContent}</Card>;
};

export default PersonForm;
